import React, { FunctionComponent, useState, useCallback, ReactNode } from 'react';
import Translate from 'ui/atoms/translate';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import WithDataRecord from 'hoc/WithDataRecord';
import { AdminApi, AdminIssuersListOrderingEnum } from 'api/apis';
import { AdminIssuerList } from 'api/models';
import StudioIssuersTable from './studio-issuers-table';
import useGoTo from 'hooks/use-go-to';
import WideContent from 'core/layout/content/wide-content';
import { ISSUER_ROUTES } from 'apps/issuer/pages/routes.config';
import Button from 'ui/atoms/button';
import AddIssuerModal from 'apps/issuer/pages/issuers/add-issuer-modal';
import { useCurrentUserSelector } from 'core/auth/hooks';
import { currentUserHasCreateIssuerPermission } from 'apps/issuer/helpers/userPermissions';
import { useDispatch } from 'store/hooks';
import { authFetch } from 'core/auth/actions';
import { hasCreateIssuerPermissionOnAtLeastOneOrganization } from '../../helpers/organizationPermissions';
import Spacer from 'src/ui/atoms/spacer';
import { useIssuerSelector } from '../../hooks';
import PageWrapper from 'src/core/layout/content/page-wrapper';

interface IssuersTableProps {
  defaultLimit: number;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  filters?: ReactNode;
  hasPermission: boolean;
  setFilterPopupHeight?: (height: number) => void;
}

const IssuerIssuersDataRecordTable: FunctionComponent<IssuersTableProps> = WithDataRecord<AdminApi, AdminIssuerList>(
  AdminApi,
)(
  ({ data, ordering, onOrderBy, loading, hasPermission, paginationProps, setFilterPopupHeight }) => {
    const goTo = useGoTo(ISSUER_ROUTES.issuerDetails);

    return (
      <>
        <StudioIssuersTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          issuers={data}
          loading={loading}
          hasPermission={hasPermission}
          goToDetails={(issuerId: string) => goTo({ issuerId })}
          paginationProps={paginationProps}
          setFilterPopupHeight={setFilterPopupHeight}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminIssuersList({
      offset,
      limit,
      ordering: ordering as AdminIssuersListOrderingEnum,
      ...props,
    });
  },
);

const IssuerIssuersPage: FunctionComponent<{}> = () => {
  const [isAddIssuerModalOpen, setAddIssuerModalOpen] = useState(false);
  const [forceDataLoad, setForceDataLoad] = useState(false);
  const [filterPopupHeight, setFilterPopupHeight] = useState(0);

  const { currentUser } = useCurrentUserSelector();
  const dispatch = useDispatch();

  const { hasIssuersToView } = useIssuerSelector();

  const hasPermission =
    !!hasIssuersToView ||
    currentUserHasCreateIssuerPermission(currentUser?.permissions || []) ||
    hasCreateIssuerPermissionOnAtLeastOneOrganization(currentUser);

  const refreshTableData = useCallback(() => {
    setForceDataLoad(true);
    setAddIssuerModalOpen(false);
    dispatch(authFetch());
  }, []);

  if (!currentUser) return null;

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <WideContent title={<Translate name="dashboardIssuerIssuers.title" />} titleSpacing="larger">
        {(currentUserHasCreateIssuerPermission(currentUser.permissions) ||
          hasCreateIssuerPermissionOnAtLeastOneOrganization(currentUser)) && (
          <>
            <Button
              variant="primary"
              icon="plus"
              size="small"
              iconSize="small"
              iconColor="inherit"
              onClick={() => setAddIssuerModalOpen(true)}
            >
              <Translate name="studioIssuers.addIssuerButton" />
            </Button>
            <Spacer y={4} />
          </>
        )}
        <IssuerIssuersDataRecordTable
          defaultLimit={50}
          forceDataLoad={forceDataLoad}
          setForceDataLoad={setForceDataLoad}
          hasPermission={hasPermission}
          setFilterPopupHeight={setFilterPopupHeight}
        />
        {isAddIssuerModalOpen && (
          <AddIssuerModal onClose={() => setAddIssuerModalOpen(false)} onAddNewIssuer={refreshTableData} />
        )}
      </WideContent>
    </PageWrapper>
  );
};

export default WithAuthenticatedPage(IssuerIssuersPage, AppType.ISSUER);
